import { Link, useOutletContext } from "react-router-dom";
import { useClientPortalPaths } from "../../hooks/useClientPortalBase";
import useClientPortalDashboard, {
  getStoredActiveClientViewId,
} from "../../hooks/useClientPortalDashboard";
import {
  ClientPortalEmpty,
  ClientPortalError,
  ClientPortalLoading,
} from "../../components/clientPortal/ClientPortalStates";
import { formatClientDate, formatClientDateTime } from "../../utils/clientPortalFormat";

function getInitials(name, email) {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  if (email) {
    return email.slice(0, 2).toUpperCase();
  }
  return "CP";
}

function statusBadgeClass(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "new") return "amber";
  if (normalized === "contacted") return "blue";
  if (normalized === "qualified" || normalized === "assigned") return "green";
  if (normalized === "closed_won" || normalized === "won") return "green";
  return "blue";
}

function publishBadgeClass(status) {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "PENDING_REVIEW") return "amber";
  if (normalized === "DRAFT") return "purple";
  if (normalized === "PUBLISHED") return "green";
  return "blue";
}

function formatRelativeDate(value) {
  if (!value) return "Recently";
  const date = new Date(value);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return "Submitted today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return formatClientDate(value);
}

function QuickActionCard({ icon, title, description, badge, badgeClass, to }) {
  const content = (
    <>
      <div>
        <div className="action-icon">{icon}</div>
        <h4>{title}</h4>
        <p>{description}</p>
      </div>
      {badge ? <span className={`badge ${badgeClass || "blue"}`}>{badge}</span> : null}
    </>
  );

  if (!to) {
    return (
      <article className="action-card is-disabled" aria-disabled="true">
        {content}
        <span className="badge purple">Coming soon</span>
      </article>
    );
  }

  return (
    <Link to={to} className="action-card">
      {content}
    </Link>
  );
}

const ClientDashboardPage = () => {
  const outletContext = useOutletContext() || {};
  const paths = useClientPortalPaths();
  const initialViewId = outletContext.activeViewId || getStoredActiveClientViewId();
  const { dashboard, loading, errorMessage, selectedViewId, setSelectedViewId } =
    useClientPortalDashboard(initialViewId);

  const viewId = dashboard.activeClientView?.id || selectedViewId;
  const viewBase = viewId ? paths.view(viewId) : null;
  const orgName = dashboard.clientOrg?.name || "your organization";
  const orgInitials = getInitials(orgName, dashboard.clientUser?.email);

  if (loading) {
    return <ClientPortalLoading message="Loading dashboard…" />;
  }

  if (errorMessage) {
    return <ClientPortalError message={errorMessage} />;
  }

  if (!dashboard.activeClientView && dashboard.assignedClientViews.length === 0) {
    return (
      <ClientPortalEmpty
        title="No Client Views assigned"
        message="No Client Views have been assigned to your account yet. Contact your account manager for access."
      />
    );
  }

  const metrics = dashboard.metrics;
  const siteStatus = dashboard.siteStatus;
  const isLive = String(siteStatus.siteStatusLabel || "").toLowerCase() === "live";
  const publishingLabel =
    siteStatus.publishingQueueCount === 1
      ? "1 pending review"
      : `${siteStatus.publishingQueueCount} pending reviews`;
  const lastPublishedLabel = siteStatus.lastPublishedAt
    ? formatClientDateTime(siteStatus.lastPublishedAt)
    : "Not published yet";

  return (
    <>
      <header className="topbar">
        <div className="topbar-left">
          <div className="eyebrow">Client Portal</div>
          <h1>Welcome back, {orgName}</h1>
          <p>
            Manage your golf communities, courses, opportunities, publishing queue, and performance
            reporting from one clean workspace.
          </p>
          {dashboard.assignedClientViews.length > 1 ? (
            <label className="empty-state" style={{ display: "block", marginTop: "12px" }}>
              Active view:{" "}
              <select
                aria-label="Select dashboard Client View"
                value={viewId || ""}
                onChange={(event) => setSelectedViewId(event.target.value)}
              >
                {dashboard.assignedClientViews.map((view) => (
                  <option key={view.id} value={view.id}>
                    {view.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>

        <div className="topbar-actions">
          {/* TODO: Wire global client portal search across opportunities, communities, and courses. */}
          <div className="search">
            <span aria-hidden="true">⌕</span>
            <input
              type="search"
              placeholder="Search leads, communities, courses..."
              aria-label="Search leads, communities, and courses"
              disabled
            />
          </div>
          <button className="profile-button" type="button" aria-label="Account">
            <div className="avatar">{orgInitials}</div>
            <span>Account</span>
          </button>
        </div>
      </header>

      {dashboard.activeClientView ? (
        <>
          <section className="hero-panel">
            <div className="hero-copy">
              <h2>Your site is active and generating opportunities.</h2>
              <p>
                This portal is designed around the work clients care about most: keeping public
                content fresh, responding to new opportunities, and understanding which communities
                and courses are creating value.
              </p>

              <div className="hero-actions">
                {viewBase ? (
                  <Link to={`${viewBase}/opportunities`} className="btn btn-primary">
                    Review New Opportunities
                  </Link>
                ) : (
                  <button type="button" className="btn btn-primary" disabled>
                    Review New Opportunities
                  </button>
                )}
                {viewBase ? (
                  <Link to={`${viewBase}/communities`} className="btn btn-secondary">
                    Manage Communities
                  </Link>
                ) : (
                  <button type="button" className="btn btn-secondary" disabled>
                    Manage Communities
                  </button>
                )}
                {dashboard.activeClientView.publicUrl ? (
                  <a
                    href={dashboard.activeClientView.publicUrl}
                    className="btn btn-secondary"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Preview Public Site
                  </a>
                ) : (
                  <button type="button" className="btn btn-secondary is-disabled" disabled>
                    Preview Public Site
                  </button>
                )}
              </div>
            </div>

            <div className="site-status">
              <div className="status-row">
                <span>Site Status</span>
                <span className={`badge ${isLive ? "green" : "amber"}`}>
                  ● {siteStatus.siteStatusLabel}
                </span>
              </div>
              <div className="status-row">
                <span>Publishing Queue</span>
                <strong>{publishingLabel}</strong>
              </div>
              <div className="status-row">
                <span>Last Published</span>
                <strong>{lastPublishedLabel}</strong>
              </div>
              <div className="status-row">
                <span>Client View</span>
                <span className="badge blue">{dashboard.activeClientView.name}</span>
              </div>
            </div>
          </section>

          <section className="metrics-grid" aria-label="Client portal metrics">
            <article className="metric-card">
              <div className="label">New Opportunities</div>
              <div className="value">{metrics.newOpportunities}</div>
              <div className="sub">Generated in the last 30 days</div>
              {metrics.opportunityTrend ? (
                <div
                  className={`trend ${metrics.opportunityTrend.direction === "up" ? "up" : "warn"}`}
                >
                  {metrics.opportunityTrend.label}
                </div>
              ) : (
                <div className="trend">Tracking lead volume</div>
              )}
            </article>

            <article className="metric-card">
              <div className="label">Needs Follow-Up</div>
              <div className="value">{metrics.needsFollowUp}</div>
              <div className="sub">New or waiting on response</div>
              <div className={`trend ${metrics.needsFollowUp > 0 ? "warn" : ""}`}>
                {metrics.needsFollowUp > 0 ? "Action recommended" : "All caught up"}
              </div>
            </article>

            <article className="metric-card">
              <div className="label">Communities</div>
              <div className="value">{metrics.totalCommunities}</div>
              <div className="sub">
                {metrics.activeCommunities} active, {metrics.draftCommunities} drafts
              </div>
              <div className="trend up">Community content overview</div>
            </article>

            <article className="metric-card">
              <div className="label">Courses</div>
              <div className="value">{metrics.totalCourses}</div>
              <div className="sub">
                {metrics.activeCourses} active, {metrics.pendingCourses} pending review
              </div>
              <div className="trend up">Course content overview</div>
            </article>
          </section>

          <section className="content-grid">
            <div>
              <section className="panel panel-spaced">
                <div className="panel-header">
                  <div>
                    <h3>What would you like to manage?</h3>
                    <p>Common client actions grouped by business outcome.</p>
                  </div>
                </div>

                <div className="panel-body">
                  <div className="quick-actions">
                    <QuickActionCard
                      icon="🎯"
                      title="Opportunities"
                      description="Review new leads, update statuses, add notes, and track follow-up."
                      badge={
                        metrics.needsFollowUp > 0
                          ? `${metrics.needsFollowUp} need attention`
                          : `${metrics.newOpportunities} recent`
                      }
                      badgeClass={metrics.needsFollowUp > 0 ? "amber" : "blue"}
                      to={viewBase ? `${viewBase}/opportunities` : null}
                    />
                    <QuickActionCard
                      icon="🏘️"
                      title="Communities"
                      description="Edit descriptions, amenities, images, SEO fields, and public visibility."
                      badge={`${metrics.totalCommunities} total`}
                      to={viewBase ? `${viewBase}/communities` : null}
                    />
                    <QuickActionCard
                      icon="⛳"
                      title="Courses"
                      description="Manage course details, community relationships, location, and features."
                      badge={`${metrics.totalCourses} total`}
                      to={viewBase ? `${viewBase}/courses` : null}
                    />
                    <QuickActionCard
                      icon="📝"
                      title="Publishing"
                      description="Preview drafts, submit updates for review, and track approvals."
                      badge={
                        siteStatus.publishingQueueCount > 0
                          ? `${siteStatus.publishingQueueCount} pending`
                          : "Up to date"
                      }
                      badgeClass={siteStatus.publishingQueueCount > 0 ? "purple" : "green"}
                      to={viewBase ? `${viewBase}/publishing` : null}
                    />
                    <QuickActionCard
                      icon="📊"
                      title="Analytics"
                      description="See lead trends, top communities, top courses, and source performance."
                      badge="View reports"
                      badgeClass="green"
                      to={viewBase ? `${viewBase}/analytics` : null}
                    />
                    <QuickActionCard
                      icon="⚙️"
                      title="Team & Settings"
                      description="Invite employees and manage who can access the Client Portal."
                      badge="Manage team"
                      to={viewBase ? `${viewBase}/settings` : null}
                    />
                  </div>
                </div>
              </section>

              <section className="panel panel-spaced">
                <div className="panel-header">
                  <div>
                    <h3>Recent Opportunities</h3>
                    <p>Client-safe view of leads generated from your Client View.</p>
                  </div>
                  {viewBase ? (
                    <Link to={`${viewBase}/opportunities`} className="small-btn">
                      View All
                    </Link>
                  ) : null}
                </div>

                <div className="panel-body">
                  {dashboard.recentOpportunities.length === 0 ? (
                    <p className="empty-state">No opportunities have been generated yet.</p>
                  ) : (
                    <div className="lead-list">
                      {dashboard.recentOpportunities.map((opp) => (
                        <article key={opp.opportunityId} className="lead-item">
                          <div>
                            <h4>{opp.title || "Opportunity inquiry"}</h4>
                            <div className="lead-meta">
                              {opp.contactName ? <span>{opp.contactName}</span> : null}
                              {opp.opportunityType ? <span>{opp.opportunityType}</span> : null}
                              {opp.budgetRange ? <span>{opp.budgetRange}</span> : null}
                              <span>{formatRelativeDate(opp.submittedAt)}</span>
                            </div>
                          </div>
                          <div className="lead-actions">
                            <span className={`badge ${statusBadgeClass(opp.status)}`}>
                              {opp.statusLabel || opp.status}
                            </span>
                            {viewBase ? (
                              <Link
                                to={`${viewBase}/opportunities/${opp.opportunityId}`}
                                className="small-btn"
                              >
                                Open
                              </Link>
                            ) : null}
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </div>
              </section>

              <section className="panel">
                <div className="panel-header">
                  <div>
                    <h3>Publishing Queue</h3>
                    <p>Track drafts, submissions, approvals, and recently published updates.</p>
                  </div>
                  {viewBase ? (
                    <Link to={`${viewBase}/publishing`} className="small-btn">
                      Open Queue
                    </Link>
                  ) : null}
                </div>

                <div className="panel-body">
                  {dashboard.publishingQueue.length === 0 ? (
                    <p className="empty-state">No publishing items in queue.</p>
                  ) : (
                    <div className="publish-grid">
                      {dashboard.publishingQueue.map((item) => (
                        <article key={item.contentVersionId} className="publish-card">
                          <span className={`badge ${publishBadgeClass(item.publishStatus)}`}>
                            {item.publishStatusLabel}
                          </span>
                          <strong>{item.title}</strong>
                          <p>
                            {item.changeSummary ||
                              `${item.contentType === "course" ? "Course" : "Community"} update`}
                          </p>
                        </article>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            </div>

            <aside>
              <section className="panel panel-spaced">
                <div className="panel-header">
                  <div>
                    <h3>Lead Pipeline</h3>
                    <p>Current opportunity status.</p>
                  </div>
                </div>

                <div className="panel-body">
                  {dashboard.pipeline.length === 0 ? (
                    <p className="empty-state">No pipeline data available yet.</p>
                  ) : (
                    <div className="progress-list">
                      {dashboard.pipeline.map((stage) => (
                        <div key={stage.key} className="progress-row">
                          <div className="progress-row-top">
                            <span>{stage.label}</span>
                            <span>{stage.count}</span>
                          </div>
                          <div className="progress-track">
                            <div
                              className="progress-fill"
                              style={{ width: `${stage.percentage}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>

              <section className="panel panel-spaced">
                <div className="panel-header">
                  <div>
                    <h3>Top Interest</h3>
                    <p>Where visitors are engaging.</p>
                  </div>
                </div>

                <div className="panel-body">
                  {dashboard.topInterest.length === 0 ? (
                    <p className="empty-state">Engagement insights will appear as activity grows.</p>
                  ) : (
                    <div className="activity-list">
                      {dashboard.topInterest.map((item) => (
                        <div key={`${item.kind}-${item.name}`} className="activity-item">
                          <div className="activity-dot">{item.icon}</div>
                          <div>
                            <strong>{item.name}</strong>
                            <span>{item.detail}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>

              <section className="panel">
                <div className="panel-header">
                  <div>
                    <h3>Recent Activity</h3>
                    <p>Latest client-visible updates.</p>
                  </div>
                </div>

                <div className="panel-body">
                  {dashboard.recentActivity.length === 0 ? (
                    <p className="empty-state">No recent activity to display.</p>
                  ) : (
                    <div className="activity-list">
                      {dashboard.recentActivity.map((item, index) => (
                        <div
                          key={`${item.occurredAt || item.title}-${index}`}
                          className="activity-item"
                        >
                          <div className="activity-dot">{item.icon}</div>
                          <div>
                            <strong>{item.title}</strong>
                            <span>{item.description}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            </aside>
          </section>
        </>
      ) : null}

      <div className="footer-note">
        Midpoint Client Portal — manage content, respond to opportunities, and track performance.
      </div>
    </>
  );
};

export default ClientDashboardPage;
